import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { useEnumLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import type { InstitutionGroup } from '@/types/platform';
import { GroupOnboardingWizard } from './GroupOnboardingWizard';
import styles from './platform-pages.module.css';

/**
 * F1 — the register of institution groups.
 *
 * A group is a school group, education authority, university system or
 * administrative grouping that several institutions belong to. This register
 * existed in core for as long as the membership API did, but nothing on the
 * console ever listed it — the only way to meet a group was to open an
 * institution and read its membership. An operator working an education
 * authority with fifty schools had no way to see the authority, let alone the
 * schools in it.
 *
 * MEMBER COUNTS ARE A PROJECTION, NOT A COLUMN. The group row has no member
 * count; the analytics endpoint computes one. Counting on the list page would
 * mean fifty extra requests, so each count is fetched once per group on load
 * and a group whose analytics call fails (the endpoint is being stood up in
 * parallel with this screen) simply shows an em dash — never a zero, which
 * would read as "empty" and could send an operator off to investigate a group
 * that is perfectly fine.
 *
 * CREATING A GROUP IS THE ONBOARDING WIZARD, NOT A SINGLE DIALOG. A group
 * usually arrives with its institutions, its administrators and its product
 * choices; the wizard collects all of it in one pass and executes it in
 * order, then lands the operator on the group they just made. The old
 * create-only dialog is gone — the register never wanted to raise bare
 * groups into the world, and the wizard's first step is exactly that dialog
 * anyway.
 */

export function GroupsPage() {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const { state, reload, replace } = useAsyncData<InstitutionGroup[]>(
    'groups',
    useCallback(() => platformApi.structure.listGroups(), []),
  );

  // groupId -> member count, from the analytics projection. Undefined means
  // "not available", not "zero" — see the comment above.
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (state.status !== 'ready') return;
    let cancelled = false;
    Promise.allSettled(
      state.data.map((group) =>
        platformApi.structure.groupAnalytics(group.id).then((analytics) => ({
          id: group.id,
          count: analytics.institutionCount,
        })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const counts: Record<string, number> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') counts[result.value.id] = result.value.count;
      }
      setMemberCounts(counts);
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  const onCompleted = (group: InstitutionGroup) => {
    if (state.status === 'ready') replace([...state.data, group]);
    setCreateOpen(false);
    navigate(`/admin/groups/${encodeURIComponent(group.id)}`);
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.groups.title')}</h1>
          <p className={styles.pageLead}>{t('platform.groups.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreateOpen(true)}>
          <Icon name="plus" size={16} />
          {t('platform.groups.create')}
        </button>
      </header>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.groups.emptyTitle')}
        emptyBody={t('platform.groups.emptyBody')}
      >
        {(data) => (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.tableCaption}>
                {t('platform.groups.tableCaption', { n: data.length })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('platform.groups.colName')}</th>
                  <th scope="col">{t('platform.groups.colType')}</th>
                  <th scope="col">{t('platform.groups.colMembers')}</th>
                  <th scope="col">{t('platform.groups.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((group) => (
                  <tr key={group.id}>
                    <th scope="row" className={styles.tableRowHeader}>
                      <Link
                        className={styles.rowTitleLink}
                        to={`/admin/groups/${encodeURIComponent(group.id)}`}
                      >
                        {group.name}
                      </Link>
                      <span className={styles.tableSub}>{group.code}</span>
                    </th>
                    <td>{label('structure.groupType', group.type)}</td>
                    <td>{memberCounts[group.id] ?? '—'}</td>
                    <td>
                      <span className={group.isActive === false ? styles.mutedTag : styles.okTag}>
                        {t(
                          group.isActive === false
                            ? 'platform.groups.status.INACTIVE'
                            : 'platform.groups.status.ACTIVE',
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <GroupOnboardingWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCompleted={onCompleted}
      />
    </div>
  );
}
